import { getFollowerUserIds, getFollowingUserIds } from '@db/Follows';
import { getUsersIn } from '@db/Users/Get';
import { sortByDisplayName } from '@helpers/Formatters';
import type { AppQueryKey } from '@helpers/query';
import { AppQueryDomain, AppQueryKeySegment, createQueryKey, useAppQuery } from '@helpers/query';
import type { User } from '@types';

const EMPTY_USERS: User[] = [];

export type UseFollowUsersQueryOptions = {
  /** Set false while route params are not ready. */
  enabled?: boolean;
  userId?: string;
};

type FollowUsersQueryResult = {
  loading: boolean;
  users: User[];
};

export function useFollowerUsersQuery({ enabled = true, userId }: UseFollowUsersQueryOptions) {
  return useFollowUsersQuery({
    enabled,
    queryKey: createQueryKey(AppQueryDomain.FOLLOWS, AppQueryKeySegment.FOLLOWERS, userId ?? null),
    queryFn: async () => getFollowerUserIds(userId ?? ''),
    userId,
  });
}

export function useFollowingUsersQuery({ enabled = true, userId }: UseFollowUsersQueryOptions) {
  return useFollowUsersQuery({
    enabled,
    queryKey: createQueryKey(AppQueryDomain.FOLLOWS, AppQueryKeySegment.FOLLOWING, userId ?? null),
    queryFn: async () => getFollowingUserIds(userId ?? ''),
    userId,
  });
}

function useFollowUsersQuery({
  enabled,
  queryFn,
  queryKey,
  userId,
}: {
  enabled: boolean;
  queryFn: () => Promise<string[]>;
  queryKey: AppQueryKey;
  userId?: string;
}): FollowUsersQueryResult {
  const isEnabled = enabled && Boolean(userId);
  const query = useAppQuery<User[]>({
    queryKey,
    queryFn: async () => {
      const userIds = await queryFn();
      if (userIds.length === 0) return [];

      return sortByDisplayName(await getUsersIn(userIds));
    },
    enabled: isEnabled,
    retry: false,
  });

  return {
    loading: isEnabled && query.isLoading,
    users: isEnabled ? (query.data ?? EMPTY_USERS) : EMPTY_USERS,
  };
}
